/****************************************************************************************************
 * SECTION BLOCK
 * --------------------------------------------------------------------------------------------------
 * The section block component is responsible for rendering the individual blocks of the section.
 ****************************************************************************************************/

import React from 'react';
import { Box, Stack } from '@mui/material';
import {  
  Edit,
  BackgroundImage,
  BasicPortableText,
  LayoutNodes,
} from 'src/components';
import { useDevMode } from 'src/hooks';
import { SanitySectionBlock } from 'src/types/sanity';
import { useStoreV2 } from 'src/store/StoreV2';

export interface SectionBlockProps {
  block: SanitySectionBlock | null;
}

const SectionBlock = ({ block }: SectionBlockProps) => {
  if (!block) return null;

  // * LAYOUT CONTENT SPACING FROM BRAND VALUES IN STORE
  const layout = useStoreV2().layout.blockLayout ?? null;
  const contentSpacing = useStoreV2().layout?.contentSpacing ?? ['10px'];

  {
    /********************************************************************************
     * * DEV MODE
     * ------------------------------------------------------------------------------
     * Enable or disable dev mode based on the presense of 'dev' in the query string.
     * ******************************************************************************/
  }
  const isDev = useDevMode();

  // Get Color
  const getColor = (type: 'backgroundColor' | 'overlayColor') => {
    let color;
    if (type === 'overlayColor') color = block?.overlayColor?._rawRgb ?? null;
    if (type === 'backgroundColor') color = block?.backgroundColor?._rawRgb ?? null;
    if (color) {
      return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
    }
  };

  return (
    <>
      <Box
        position={'relative'}
        width={'100%'}
        height={'100%'}
        border={isDev ? '2px dashed #CCC' : 'none'}
        sx={{
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/********************************************************************************
         * * BACKGROUND COLOR
         * ------------------------------------------------------------------------------
         * ******************************************************************************/}
        <Box
          zIndex={-3}
          position={'absolute'}
          sx={{
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: getColor('backgroundColor'),
          }}
        />

        {/********************************************************************************
         * * BACKGROUND IMAGE
         * ------------------------------------------------------------------------------
         * ******************************************************************************/}
        <BackgroundImage imageData={block.backgroundImage?._rawImage ?? null} />

        {/********************************************************************************
         * * OVERLAY COLOR
         * ------------------------------------------------------------------------------
         * ******************************************************************************/}
        <Box
          position={'absolute'}
          zIndex={-1}
          sx={{
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: getColor('overlayColor'),
          }}
        />

        <LayoutNodes layoutNodes={block.layoutNodes ?? []}>
          <Box
            className={`elo-section-blocks-root`}
            border={isDev ? '1px dashed tomato' : undefined}
            // Apply margin values from the layout data in the store
            m={layout?.margin?.m ?? undefined}
            mx={layout?.margin?.mx ?? undefined}
            my={layout?.margin?.my ?? undefined}
            mt={layout?.margin?.mt ?? undefined}
            mr={layout?.margin?.mr ?? undefined}
            mb={layout?.margin?.mb ?? undefined}
            ml={layout?.margin?.ml ?? undefined}
            // Apply padding values from the layout data in the store
            p={layout?.padding?.p ?? undefined}
            px={layout?.padding?.px ?? undefined}
            py={layout?.padding?.py ?? undefined}
            pt={layout?.padding?.pt ?? undefined}
            pr={layout?.padding?.pr ?? undefined}
            pb={layout?.padding?.pb ?? undefined}
            pl={layout?.padding?.pl ?? undefined}
          >
            <Stack spacing={contentSpacing} className="basic-block-stack">
              <BasicPortableText content={block._rawContent} />
            </Stack>
          </Box>
        </LayoutNodes>
      </Box>
      <Edit documentType="sectionBlock" documentId={block._id} />
    </>
  );
};

export default SectionBlock;
